/**
 * Fila Configuration Modal
 *
 * Modal tela inteira para configurar o sistema de fila digital
 */

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@ui/dialog';
import { Button } from '@components/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ui/card';
import { Badge } from '@ui/badge';
import { Label } from '@ui/label';
import { ToggleSwitch } from '../shared/toggle-switch';
import { RadioGroup, RadioGroupItem } from '@ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select';
import { Separator } from '@ui/separator';
import { Settings, Check, AlertCircle, Info, Store } from 'lucide-react';
import { useFilaConfig } from '../../hooks/useFilaConfig';
import type { VisibleColumns, OrderType, IntervalConfig } from '../../types/config';
import { useUserInfo } from '@queries/auth';
import { useIntervals } from '@queries/establishment';
import { toast } from 'sonner';
import { authService } from '@services/api';
import { useNavigate } from '@tanstack/react-router';

interface FilaConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRequestRefresh?: () => void;
  onWebSocketReconnect?: () => void;
}

export function FilaConfigModal({ open, onOpenChange, onRequestRefresh, onWebSocketReconnect }: FilaConfigModalProps) {
  const navigate = useNavigate();
  const { config, setVisibleColumns, setOrderType, setAutoIntervalChange, setEstablishmentId, resetConfig, isLoading, error, clearError } =
    useFilaConfig();

  const [localError, setLocalError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  const setError = (message: string) => setLocalError(message);

  // Limpar erros quando o modal abrir
  useEffect(() => {
    if (open) {
      clearError();
      setLocalError(null);
      setSavedMessage(null);
    }
  }, [open, clearError]);

  // Carregar dados do estabelecimento e intervalos
  const { data: userInfo } = useUserInfo();

  // Obter dados do estabelecimento do localStorage primeiro
  const currentEstablishment = authService.getCurrentEstablishment();

  // Encontrar o perfil ativo para obter o establishment_id como fallback
  // O perfil de /auth/userinfo (ProfileLite) não carrega establishment_id;
  // o fallback que lia dali nunca resolvia.
  const establishmentId = config.establishmentId || currentEstablishment?.id;

  // Buscar intervalos do estabelecimento da API
  const { data: intervalsData } = useIntervals(establishmentId || '');

  // Transformar dados da API para o formato esperado
  const apiIntervals: IntervalConfig[] =
    intervalsData?.map((interval: any) => ({
      id: interval.id,
      name: interval.name,
      startTime: interval.start_time,
      endTime: interval.end_time,
      isActive: interval.active,
    })) || [];

  // Função para mostrar mensagem de salvamento
  const showSavedMessage = (message: string) => {
    setSavedMessage(message);
    setTimeout(() => setSavedMessage(null), 2000);
  };

  // Fechar modal (as configurações já são salvas automaticamente)
  const handleClose = () => {
    clearError();
    setLocalError(null);

    // Disparar callbacks para atualizar requests e websocket
    if (onRequestRefresh) {
      console.log('🔄 Config modal: Triggering request refresh');
      onRequestRefresh();
    }

    if (onWebSocketReconnect) {
      console.log('🔄 Config modal: Triggering WebSocket reconnection');
      onWebSocketReconnect();
    }

    onOpenChange(false);
  };

  // Resetar configurações
  const handleReset = () => {
    if (confirm('Tem certeza que deseja resetar todas as configurações?')) {
      resetConfig();
      // Configurar establishmentId após reset
      if (establishmentId) {
        setEstablishmentId(establishmentId);
      }
      toast.success('Configurações resetadas com sucesso!');
      onOpenChange(false);
    }
  };

  // Trocar estabelecimento
  const handleTrocarEstabelecimento = () => {
    if (confirm('Tem certeza que deseja trocar de estabelecimento?')) {
      // Limpar seleção atual e navegar
      authService.clearEstablishmentSelection();
      onOpenChange(false);
      navigate({ to: '/selecionar-estabelecimento' });
    }
  };

  const columnOptions = [
    { key: 'fila' as keyof VisibleColumns, label: 'Coluna Fila', color: 'bg-brand-primary' },
    { key: 'preparando' as keyof VisibleColumns, label: 'Coluna Preparando', color: 'bg-brand-primary' },
    { key: 'pronto' as keyof VisibleColumns, label: 'Coluna Pronto', color: 'bg-brand-primary' },
  ];

  const orderTypeOptions = [
    { value: 'queue' as OrderType, label: 'Pedidos da Fila Digital', description: 'Pedidos feitos diretamente na fila' },
    { value: 'lunchbox' as OrderType, label: 'Pedidos da Lancheira Digital', description: 'Pedidos antecipados pela lancheira' },
  ];

  const activeColumnsCount = Object.values(config.visibleColumns).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-screen max-h-screen max-w-full overflow-y-auto rounded-2xl" showCloseButton={true}>
        <DialogHeader className="border-b pr-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Settings className="h-5 w-5 text-black" />
              <DialogTitle className="text-xl text-black">Configurações da Fila Digital</DialogTitle>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant="default" className="border-green-200 bg-green-100 text-green-800">
                Auto-salvamento ativo
              </Badge>
            </div>
          </div>
          <DialogDescription>As configurações são salvas automaticamente ao serem alteradas</DialogDescription>
        </DialogHeader>

        {(error || localError) && (
          <div className="mx-6 mb-4 flex items-center space-x-2 rounded-lg border border-red-200 bg-red-50 p-3">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <span className="text-sm text-red-700">{error || localError}</span>
          </div>
        )}

        {savedMessage && (
          <div className="mx-6 mb-4 flex items-center space-x-2 rounded-lg border border-green-200 bg-green-50 p-3">
            <Check className="h-4 w-4 text-green-500" />
            <span className="text-sm text-green-700">{savedMessage}</span>
          </div>
        )}

        <div className="space-y-6 px-6 py-4">
          {/* Seção 1: Colunas Visíveis */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-primary-100">
                    <span className="text-sm font-bold text-brand-primary-600">1</span>
                  </div>
                  Colunas Visíveis
                </CardTitle>
                <Badge variant="ghost">{activeColumnsCount} de 3 ativas</Badge>
              </div>
              <CardDescription>Selecione quais colunas serão exibidas na tela da fila digital</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {columnOptions.map((column) => (
                  <div
                    key={column.key}
                    className={`rounded-lg border p-4 transition-all duration-200 ${
                      config.visibleColumns[column.key] ? `border-opacity-50 bg-opacity-10` : 'border-gray-200 hover:border-gray-300'
                    }`}>
                    <div className="flex items-center space-x-3">
                      <ToggleSwitch
                        id={`column-${column.key}`}
                        checked={config.visibleColumns[column.key]}
                        onCheckedChange={(checked) => {
                          setVisibleColumns({ [column.key]: checked });
                          showSavedMessage(`${column.label} ${checked ? 'ativada' : 'desativada'}`);
                        }}
                      />
                      <div>
                        <Label htmlFor={`column-${column.key}`} className="font-medium text-gray-900">
                          {column.label}
                        </Label>
                        <p className={`text-xs ${config.visibleColumns[column.key] ? 'text-white/80' : 'text-gray-500'}`}>
                          {config.visibleColumns[column.key] ? 'Visível na tela' : 'Ocultada da tela'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Seção 2: Informações da Fila */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-primary-100">
                  <span className="text-sm font-bold text-brand-primary-600">2</span>
                </div>
                <CardTitle>Informações da Fila</CardTitle>
              </div>
              <CardDescription>Selecione qual tipo de pedidos serão exibidos</CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={config.orderType}
                onValueChange={(value: OrderType) => {
                  setOrderType(value);
                  const option = orderTypeOptions.find((opt) => opt.value === value);
                  showSavedMessage(`Tipo de pedido alterado para: ${option?.label}`);
                }}
                className="space-y-3">
                {orderTypeOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`flex cursor-pointer items-center space-x-3 rounded-lg border p-4 transition-all duration-200 ${
                      config.orderType === option.value ? 'border-brand-primary-500 bg-brand-primary-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                    <RadioGroupItem value={option.value} id={`order-${option.value}`} className="sr-only" />
                    <Label htmlFor={`order-${option.value}`} className="flex-1 cursor-pointer">
                      <div className="font-medium text-gray-900">{option.label}</div>
                      <p className="text-sm text-gray-500">{option.description}</p>
                    </Label>
                    {config.orderType === option.value && <Check className="h-5 w-5 text-brand-primary-500" />}
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Seção 3: Troca Automática de Intervalo */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-primary-100">
                  <span className="text-sm font-bold text-brand-primary-600">3</span>
                </div>
                <CardTitle>Troca Automática de Intervalo</CardTitle>
              </div>
              <CardDescription>Configure a troca automática entre intervalos do estabelecimento</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="auto-switch" className="text-sm font-medium">
                    Ativar troca automática
                  </Label>
                  <p className="text-xs text-gray-500">
                    {config.autoIntervalChange
                      ? 'O sistema alternará automaticamente entre os intervalos'
                      : 'Você precisará selecionar manualmente o intervalo'}
                  </p>
                </div>
                <ToggleSwitch
                  id="auto-switch"
                  checked={config.autoIntervalChange}
                  onCheckedChange={(checked) => {
                    setAutoIntervalChange(checked, config.selectedInterval, config.selectedIntervalName);
                    showSavedMessage(`Troca automática ${checked ? 'ativada' : 'desativada'}`);
                  }}
                />
              </div>

              {!config.autoIntervalChange && (
                <div className="space-y-2">
                  <Label htmlFor="interval-select" className="text-sm font-medium">
                    Selecionar Intervalo Atual
                  </Label>
                  <Select
                    value={config.selectedInterval || ''}
                    onValueChange={(value) => {
                      const interval = apiIntervals.find((i) => i.id === value);
                      setAutoIntervalChange(false, value, interval?.name);
                      showSavedMessage(`Intervalo alterado para: ${interval?.name || value}`);
                    }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um intervalo" />
                    </SelectTrigger>
                    <SelectContent>
                      {apiIntervals.length > 0 ? (
                        apiIntervals.map((interval) => (
                          <SelectItem key={interval.id} value={interval.id}>
                            <div className="flex w-full items-center justify-between">
                              <span>{interval.name}</span>
                              <span className="text-xs text-gray-500">
                                {interval.startTime} - {interval.endTime}
                              </span>
                            </div>
                          </SelectItem>
                        ))
                      ) : (
                        <div className="px-2 py-1 text-sm text-gray-500">Nenhum intervalo encontrado</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Seção 4: Estabelecimento */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-primary-100">
                  <span className="text-sm font-bold text-brand-primary-600">4</span>
                </div>
                <CardTitle>Estabelecimento</CardTitle>
              </div>
              <CardDescription>Gerenciar estabelecimento ativo no sistema</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <Store className="h-5 w-5 text-gray-500" />
                        <span className="font-medium text-gray-900">Estabelecimento Atual</span>
                      </div>
                      <div className="ml-7 space-y-1">
                        {currentEstablishment ? (
                          <>
                            <p className="text-sm font-medium text-gray-900">{currentEstablishment.name}</p>
                            <p className="text-xs text-gray-600">ID: {currentEstablishment.id}</p>
                            {currentEstablishment.support_code && (
                              <p className="text-xs text-gray-600">Código: #{currentEstablishment.support_code}</p>
                            )}
                          </>
                        ) : (
                          <p className="text-sm text-gray-600">Nenhum estabelecimento selecionado</p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleTrocarEstabelecimento}
                      className="border-brand-primary-200 text-brand-primary-600 hover:bg-brand-primary-50">
                      <Store className="mr-2 h-4 w-4" />
                      Trocar Estabelecimento
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
